import { useState, useEffect, useRef } from 'react';
import * as api from '../lib/api';
import type { User } from '../lib/types';

// Re-export Telegram types for backward compat
export interface TelegramWidgetUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export const TG_BROWSER_AUTH_KEY = 'tg_browser_auth';
const AUTH_MAX_AGE_DAYS = 30;

export function loadSavedBrowserAuth(): TelegramWidgetUser | null {
  try {
    const raw = localStorage.getItem(TG_BROWSER_AUTH_KEY);
    if (!raw) return null;
    const user: TelegramWidgetUser = JSON.parse(raw);
    const ageSeconds = Date.now() / 1000 - user.auth_date;
    if (ageSeconds > AUTH_MAX_AGE_DAYS * 24 * 3600) {
      localStorage.removeItem(TG_BROWSER_AUTH_KEY);
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

export function saveBrowserAuth(user: TelegramWidgetUser) {
  try {
    localStorage.setItem(TG_BROWSER_AUTH_KEY, JSON.stringify(user));
  } catch {}
}

interface Props {
  onAuth: (user: User) => void;
  onTelegramAuth: (user: TelegramWidgetUser) => void;
}

const s = {
  root: {
    display: 'flex', flexDirection: 'column' as const, justifyContent: 'center',
    alignItems: 'center', minHeight: '100vh', padding: '32px',
    textAlign: 'center' as const, background: '#060606', color: '#fff',
  },
  title: {
    fontSize: 26, fontWeight: 800, marginBottom: 6,
    fontFamily: "'Unbounded', sans-serif", color: '#f2c14b',
  },
  subtitle: { color: '#888', fontSize: 14, marginBottom: 32, lineHeight: 1.5 },
  card: {
    width: '100%', maxWidth: 360, background: 'rgba(255,255,255,0.04)',
    borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: '28px 24px',
  },
  input: {
    width: '100%', padding: '14px 16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 16, outline: 'none',
    fontFamily: 'inherit',
  },
  btn: {
    width: '100%', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer',
    background: 'linear-gradient(135deg, #f2c14b 0%, #d79a2b 100%)',
    color: '#000', fontSize: 15, fontWeight: 700, fontFamily: "'Unbounded', sans-serif",
    marginTop: 12, transition: 'opacity 0.2s',
  },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' as const },
  error: { color: '#ff4d4d', fontSize: 13, marginTop: 8 },
  divider: {
    display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0 16px',
    color: 'rgba(255,255,255,0.3)', fontSize: 12, width: '100%', maxWidth: 360,
  },
  line: { flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' },
  resend: {
    background: 'none', border: 'none', color: '#f2c14b', fontSize: 13,
    cursor: 'pointer', marginTop: 12, textDecoration: 'underline',
  },
};

export default function LoginScreen({ onAuth, onTelegramAuth }: Props) {
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [tgWidgetFailed, setTgWidgetFailed] = useState(false);

  const otpInputRef = useRef<HTMLInputElement | null>(null);
  const tgContainerRef = useRef<HTMLDivElement>(null);
  const onTelegramAuthRef = useRef(onTelegramAuth);
  onTelegramAuthRef.current = onTelegramAuth;

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Mount Telegram widget
  useEffect(() => {
    (window as any).onTelegramAuth = (user: TelegramWidgetUser) => {
      saveBrowserAuth(user);
      onTelegramAuthRef.current(user);
      setTimeout(() => {
        if (document.getElementById('login-root')) window.location.reload();
      }, 2000);
    };

    const botUsername = import.meta.env.VITE_TG_BOT_USERNAME;
    if (!botUsername) return;

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;

    script.onerror = () => setTgWidgetFailed(true);

    const container = tgContainerRef.current;
    if (container) container.appendChild(script);

    return () => {
      if (container && script.parentNode === container) container.removeChild(script);
      delete (window as any).onTelegramAuth;
    };
  }, []);

  const handleSendOtp = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Введите корректный email');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.sendEmailOtp(trimmed);
      setStep('otp');
      setCooldown(60);
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (e: any) {
      const msg = e?.message || '';
      setError(msg.includes('aborted') || msg.includes('abort') ? 'Медленное соединение, попробуйте ещё раз' : (msg || 'Ошибка отправки кода'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code?: string) => {
    const finalCode = (code || otpCode).replace(/\D/g, '');
    if (finalCode.length < 6) {
      setError('Введите код из письма');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await api.verifyEmailOtp(email.trim().toLowerCase(), finalCode);
      if (!data.session || !data.user) throw new Error('Не удалось подтвердить код');

      const userData = await api.upsertUserByEmail(
        email.trim().toLowerCase(),
        data.user.id,
      );
      onAuth({ ...userData, tg_photo_url: null });
    } catch (e: any) {
      setError(e.message === 'Token has expired or is invalid'
        ? 'Неверный или просроченный код'
        : (e.message || 'Ошибка подтверждения')
      );
      setOtpCode('');
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="login-root" style={s.root}>
      <img src="/stories-bg-1.svg" alt="HU" style={{ width: 80, height: 80, marginBottom: 12, filter: 'invert(78%) sepia(60%) saturate(500%) hue-rotate(5deg) brightness(100%) contrast(95%)' }} />
      <img src="/heads-up-text.svg" alt="HEADS UP POKER CLUB" style={{ height: 32, marginBottom: 32, filter: 'invert(78%) sepia(60%) saturate(500%) hue-rotate(5deg) brightness(100%) contrast(95%)', opacity: 0.8 }} />

      <div style={s.card}>
        {step === 'email' ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.9)' }}>
              Войти по email
            </div>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
              style={s.input}
              autoFocus
            />
            {error && <div style={s.error}>{error}</div>}
            <button
              onClick={handleSendOtp}
              disabled={loading || !email.trim()}
              style={{ ...s.btn, ...(loading || !email.trim() ? s.btnDisabled : {}) }}
            >
              {loading ? 'Отправка...' : 'Получить код'}
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'rgba(255,255,255,0.9)' }}>
              Введите код
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 20 }}>
              Отправлен на {email}
            </div>
            <input
              ref={otpInputRef}
              type="text"
              inputMode="numeric"
              placeholder="Код"
              value={otpCode}
              onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '')); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
              style={{ ...s.input, textAlign: 'center', fontSize: 24, fontWeight: 700, letterSpacing: 6 }}
              autoFocus
            />
            {error && <div style={s.error}>{error}</div>}
            <button
              onClick={() => handleVerifyOtp()}
              disabled={loading || otpCode.length < 6}
              style={{ ...s.btn, ...(loading || otpCode.length < 6 ? s.btnDisabled : {}) }}
            >
              {loading ? 'Проверка...' : 'Войти'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button
                style={{ ...s.resend, color: 'rgba(255,255,255,0.4)' }}
                onClick={() => { setStep('email'); setOtpCode(''); setError(''); }}
              >
                ← Назад
              </button>
              <button
                style={{ ...s.resend, opacity: cooldown > 0 ? 0.4 : 1, cursor: cooldown > 0 ? 'not-allowed' : 'pointer' }}
                onClick={() => { if (cooldown <= 0) handleSendOtp(); }}
                disabled={cooldown > 0}
              >
                {cooldown > 0 ? `Повторить (${cooldown}с)` : 'Отправить ещё раз'}
              </button>
            </div>
          </>
        )}
      </div>

      {!tgWidgetFailed && (
        <>
          {/* Divider */}
          <div style={s.divider}>
            <div style={s.line} />
            <span>или</span>
            <div style={s.line} />
          </div>

          {/* Telegram widget */}
          <div ref={tgContainerRef} />
        </>
      )}
    </div>
  );
}
